import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte,
    AanmerkelijkBelang,
    BokkzAlgDataService,
    IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BokkzIbDataService } from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { optionsLandcodesMetNLD } from '../../gedeelde-componenten/gedeelde-componenten.component';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';
import { CommonModule } from '@angular/common';
import { SharedUiAlertModule } from '@snelstart/ui-alert';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box2-inkomen-uit-aanmerkelijk-belang',
    templateUrl: './box2-inkomen-uit-aanmerkelijk-belang.component.html',
    styleUrls: ['./box2-inkomen-uit-aanmerkelijk-belang.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDialogModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        SharedUiTooltipModule,
        CommonModule,
        SharedUiAlertModule
    ]
})
export class Box2InkomenUitAanmerkelijkBelangComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    @Input() partner = new BehaviorSubject<boolean>(false);
    private destroy$ = new Subject<void>();
    private isSaving = false;
    public clientId!: string;
    public incomeTaxEntity = {} as IncomeTaxEntity;
    public aanmerkelijkBelangFg = {} as FormGroup;
    public focusedElementId: string | null = null;
    public hasBuitenlandsLandcode = false;
    public dialogVerkoopVisible = false;
    public dialogLenenVisible = false;
    public index = 0;

    public itemsVerkrijgingsprijs: SelectFormControlItem<boolean | undefined>[] = [
        { value: undefined, label: '--- selecteer ---' },
        { value: false, label: 'Niet doorschuiven' },
        { value: true, label: 'Wel doorschuiven' }
    ];

    public itemsBeleggingsvermogen: SelectFormControlItem<boolean | undefined>[] = [
        { value: undefined, label: '--- selecteer ---' },
        { value: false, label: 'Geen beleggingsvermogen' },
        { value: true, label: 'Wel beleggingsvermogen' }
    ];

    public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    constructor(public dataService: BokkzIbDataService,
                public dataAlgService: BokkzAlgDataService,
                private router: Router,
                private fb: FormBuilder) {
        this.aanmerkelijkBelangFg = this.fb.group({
            aanmerkelijkBelang: this.fb.array<AanmerkelijkBelang>([]),
            eerderInHeffingBetrokkenSchuldBijVennootschappen: null as unknown as number
        });
    }

    ngOnInit(): void {
        this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                this.updateForm();
            }
        });
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    showDialogVerkoop(index: number) {
        this.index = index;
        this.dialogVerkoopVisible = true;
    }

    showDialogLenen(index: number) {
        this.index = index;
        this.dialogLenenVisible = true;
    }

    get aanmerkelijkBelang(): FormArray {
        return this.aanmerkelijkBelangFg.get('aanmerkelijkBelang') as FormArray;
    }

    addRij(): void {
        this.aanmerkelijkBelang.push(this.createFormGroup());
    }

    verwijderRij(index: number): void {
        this.aanmerkelijkBelang.removeAt(index);
        this.aanmerkelijkBelangFg.updateValueAndValidity();
        this.aanmerkelijkBelangFg.markAsDirty();
        if (!this.heeftTotaalSchulden) {
            this.aanmerkelijkBelangFg
                .get('eerderInHeffingBetrokkenSchuldBijVennootschappen')
                ?.setValue(undefined);
        }
        this.saveForm();
    }

    createFormGroup(ab?: AanmerkelijkBelang): FormGroup {
        return this.fb.group(({
            omschrijving: ab ? ab.omschrijving : undefined,
            aantalAandelen: ab ? ab.aantalAandelen : undefined,
            landCode: ab ? ab.landCode : undefined,
            reguliereVoordelen: ab ? ab.reguliereVoordelen : undefined,
            aftrekbareKosten: ab ? ab.aftrekbareKosten : undefined,
            overdrachtsprijs: ab ? ab.overdrachtsprijs : undefined,
            toerekenbareVerkrijgingsprijs: ab ? ab.toerekenbareVerkrijgingsprijs : undefined,
            verzoekDoorschuivenVerkrijgingsPrijs: ab ? ab.verzoekDoorschuivenVerkrijgingsPrijs : undefined,
            vennootschapBevatBeleggingsVermogen: ab ? ab.vennootschapBevatBeleggingsVermogen : undefined,
            totaalSchuldenBijVennootschappen: ab ? ab.totaalSchuldenBijVennootschappen : undefined,
            eigenWoningSchuldenBijVennootschappen: ab ? ab.eigenWoningSchuldenBijVennootschappen : undefined
        } as unknown as AanmerkelijkBelang));
    }

    checkBuitenlandsLandcode(): void {
        const aanmerkelijkBelangArray = this.aanmerkelijkBelang.controls;
        for (const item of aanmerkelijkBelangArray) {
            const landCode = item.get('landCode')?.value;
            if (landCode && landCode !== 'NLD') {
                this.hasBuitenlandsLandcode = true;
                return;
            }
        }
        this.hasBuitenlandsLandcode = false;
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.aanmerkelijkBelangFg.dirty;
    }

    private updateForm(): void {
        const aanmerkelijkBelangLijst = this.incomeTaxEntity?.aangifte?.box2?.aanmerkelijkBelang || [];
        const formArray = this.aanmerkelijkBelangFg.get('aanmerkelijkBelang') as FormArray;
        const focusedControl = this.focusedElementId ? (document.getElementById(this.focusedElementId) as HTMLElement) : null;

        while (formArray.length > aanmerkelijkBelangLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < aanmerkelijkBelangLijst.length; i++) {
            const abItem = aanmerkelijkBelangLijst[i];
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(abItem);
            } else {
                const formGroup = this.createFormGroup(abItem);
                formArray.push(formGroup);
            }
        }

        this.aanmerkelijkBelangFg.patchValue({
            eerderInHeffingBetrokkenSchuldBijVennootschappen:
                this.incomeTaxEntity?.aangifte?.box2?.['eerderInHeffingBetrokkenSchuldBijVennootschappen'] ?? null
        });
        this.checkBuitenlandsLandcode();

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        this.aanmerkelijkBelangFg.updateValueAndValidity();
        this.incomeTaxEntity.aangifte = {
            ...this.incomeTaxEntity.aangifte,
            box2: {
                ...this.incomeTaxEntity.aangifte?.box2,
                aanmerkelijkBelang: this.aanmerkelijkBelangFg.getRawValue().aanmerkelijkBelang,
                eerderInHeffingBetrokkenSchuldBijVennootschappen: this.aanmerkelijkBelangFg.value.eerderInHeffingBetrokkenSchuldBijVennootschappen
            }
        } as Aangifte;
        this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    }

    get heeftTotaalSchulden(): boolean {
        return this.aanmerkelijkBelang.controls.some(control => {
            const value = control.get('totaalSchuldenBijVennootschappen')?.value;
            return value !== null && value !== undefined && value !== 0;
        });
    }

}
